/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantService } from '@vsolv/core/multi-tenant/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { MenuTriggerForDirective } from '@vsolv/vectors-ui/menu';
import { Link } from '@wsphere/links/domain';
import { LinksWebService } from '@wsphere/links/web';
import { Claim, Coverage } from '@wsphere/warranties/domain';
import { map, switchMap, tap } from 'rxjs';
import { ClaimService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-details-dialog',
  templateUrl: './claim-item-details.dialog.html',
  standalone: false,
})
export class ClaimItemDetailsDialog {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private claimsSvc: ClaimService,
    private formBuilder: FormBuilder,
    private linkSvc: LinksWebService,
    private tenantSvc: TenantService
  ) {}

  @ViewChild('claimItemDetails') claimItemDetailsDialog!: DialogComponent;
  @ViewChild('removeDialog') removeDialog!: DialogComponent;

  @Input() showPicker = true;
  @Output() closed = new EventEmitter();
  readonly isCustomerPortal$ = this.route.data.pipe(map(data => data['isCustomerPortal'] as boolean));
  readonly tenantId = this.tenantSvc.tenantId;
  readonly claim$ = this.claimsSvc.getClaim();
  today = new Date();
  selectedDate: Date | null = null;
  item: Claim.ClaimItem.Model | null = null;
  item$ = this.route.data.pipe(
    map(data => data['item'] as Claim.ClaimItem.Model),
    tap(item => {
      if (!item) return;

      this.item = item;
      this.form.patchValue({
        coverage: item.coverage,
        description: item.description,
        dateOfPurchase: item.dateOfPurchase ?? null, // new for HWG only
      });
      this.selectedDate = item.dateOfPurchase ? new Date(item.dateOfPurchase) : null;
    })
  );

  links$ = this.item$.pipe(
    switchMap(async item =>
      item
        ? await this.linkSvc.list(
            { id: item.claimId, objectType: Link.ObjectType.CLAIM },
            { owner: { id: item.id, objectType: Link.ObjectType.CLAIM_ITEM } }
          )
        : null
    ),
    map(data => data?.items),
    tap(links => {
      if (this.item && links?.length) {
        this.links = links;

        const originalLinks = Link.getPickerLinks(links, Link.ObjectType.CLAIM_ITEM, this.item.id);
        if (originalLinks?.length) {
          this.originalLinks = originalLinks;

          this.form.patchValue({
            links: originalLinks.map(link => ({ id: link.id, object: link.object, type: link.type })),
          });
        }
      }
    })
  );

  removing = false;
  loading = false;

  // Only tell the overview to refresh when the item actually changed, not on a plain close.
  saved = false;

  links: Link.Model[] | null = null;
  originalLinks: Link.FormLink[] | null = null;

  draft = Claim.ClaimItem.Status.DRAFT;

  form = this.formBuilder.group({
    coverage: ['empty' as Coverage.Model | null | string, Validators.required],
    description: [''],
    links: [[] as Link.FormLink[]],
    dateOfPurchase: [null as string | null, [Validators.required, this.validatePastDate]],
  });

  validatePastDate(control: AbstractControl): ValidationErrors | null {
    const selected = new Date(control.value);
    const today = new Date();
    return selected > today ? { futureDate: true } : null;
  }

  populateDate(event: Date, menu: MenuTriggerForDirective) {
    if (!event) {
      // User clicked "Clear Selection"
      this.form.patchValue({ dateOfPurchase: null });
      this.selectedDate = null;
      this.showPicker = false;
      setTimeout(() => (this.showPicker = true), 0);
      menu.close();
      return;
    }
    if (event.getTime() > Date.now()) {
      this.toastSvc.show({
        type: 'error',
        title: 'Invalid Date',
        text: 'Please select a date in the past and try again!',
      });
    } else {
      const localDate = [
        event.getFullYear(),
        (event.getMonth() + 1).toString().padStart(2, '0'),
        event.getDate().toString().padStart(2, '0'),
      ].join('-');
      this.selectedDate = new Date(localDate);
      this.form.patchValue({ dateOfPurchase: localDate });
      menu.close();
    }
  }

  openDialog() {
    this.claimItemDetailsDialog?.open();
  }

  closeDialog() {
    this.form.reset({ coverage: 'empty' });
    this.selectedDate = null;
    this.claimItemDetailsDialog.close();
  }

  openRemoveDialog() {
    this.removing = true;
    this.claimItemDetailsDialog.close();
    this.removeDialog?.open();
  }

  closeRemoveDialog(action?: string) {
    this.removing = false;
    if (action !== 'deleted') this.claimItemDetailsDialog.open();
    else if (action === 'deleted') {
      this.saved = true;
      this.navigateTo('../..');
    }
  }

  async save(claim: Claim.Model | null) {
    if (!claim || (!this.form.value.coverage && this.form.value.coverage !== null)) return;

    const value = this.form.value;

    const payload: any = {
      coverageId: (value.coverage as Coverage.Model)?.id ?? null,
      description: value.description,
    };

    // shown only to Headstart (HWG) tenant
    if (this.tenantId === 'tnt_b490a7f051ab4cd8a6695b3c036ebcad') {
      payload.dateOfPurchase = value.dateOfPurchase;
    }
    if (this.item) {
      const response = await this.claimsSvc
        .updateItem(claim.id, this.item.id, payload)
        .catch((err: { error: { message: any } }) => {
          this.toastSvc.show({
            type: 'error',
            title: 'Something went wrong',
            text: `${err?.error?.message}`,
          });

          this.closeDialog();
        });

      if (response) {
        this.updateLinks(response.id);

        this.toastSvc.show({
          type: 'success',
          title: 'Claim item updated',
          text: `${(value.coverage as Coverage.Model)?.title || 'Other'} item has been successfully updated.`,
        });

        this.saved = true;
        this.closeDialog();
        this.closed.emit();
      }
    } else {
      const response = await this.claimsSvc.createItem(claim.id, payload).catch((err: { error: { message: any } }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: `${err?.error?.message}`,
        });

        this.closeDialog();
      });

      if (response) {
        this.updateLinks(response.id);

        this.toastSvc.show({
          type: 'success',
          title: 'Claim item created',
          text: `${
            (value.coverage as Coverage.Model)?.title || 'Other'
          } item has been successfully created under Claim #${claim.claimNumber}.`,
        });

        this.saved = true;
        this.closeDialog();
        this.closed.emit();
      }
    }
  }

  async updateLinks(itemId: string) {
    const links = this.form.value.links;

    // Add new links
    if (links?.length) {
      const originalLinkIds = this.originalLinks?.map(link => link.id);

      await Promise.all(
        links.map(async link => {
          if (!originalLinkIds?.includes(link.id)) {
            await this.linkSvc.create(
              { id: itemId, objectType: Link.ObjectType.CLAIM_ITEM },
              {
                object1: { id: (link.object as any).id, objectType: link.type },
                object2: { id: itemId, objectType: Link.ObjectType.CLAIM_ITEM },
              }
            );
          }
        })
      );
    }

    // Remove links
    if (this.originalLinks?.length) {
      const newLinks = links?.map(link => (link.object as any)?.id);

      await Promise.all(
        this.originalLinks?.map(async link => {
          if (link.id && !newLinks?.includes((link.object as any)?.id)) {
            await this.linkSvc.delete({ id: itemId, objectType: Link.ObjectType.CLAIM_ITEM }, link.id);
          }
        })
      );
    }
  }

  navigateTo(path?: string) {
    const saved = this.saved;
    this.saved = false;
    this.router.navigate([path ?? '..'], {
      relativeTo: this.route,
      ...(saved ? { queryParams: { closeItemDialog: true } } : {}),
    });
  }
}
